import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import {
    describeUnavailableTemplateFont,
    getTemplateFontIndex,
    resolveTemplateFontFamily,
} from '../video-state';

import { resolveSceneById, sceneDisplayId } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import {
    getCaptionsSceneDesignsForTemplate,
    getTemplateTextDefaultSizes,
    getTemplateTextMaxSize,
    getTemplateTextMinSize,
} from 'js/src/data/templates/templates-selectors';
import { parseHexColor } from 'js/src/libs/color-utils';
import type { RootState } from 'js/src/libs/RootState';
import {
    firstCaptionAsset,
    isVoiceDrivenSceneGroup,
    setAssetPositionTransformationOperation,
    setCaptionsColorOperation,
    setCaptionsFontUrlOperation,
    setCaptionsSceneDesignOperation,
    setCaptionsTextSizeOperation,
    setHasUserChangedAssetPositionOperation,
    setSceneDisplayCaptionsOperation,
    setSceneGroupCaptionsVisibilityOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

const CAPTION_POSITIONS = ['top', 'center', 'bottom'] as const;
type CaptionPosition = (typeof CAPTION_POSITIONS)[number];

const TEXT_COLOR_PROPERTY = 'normalStyle.fill';
const SPOKEN_WORD_COLOR_PROPERTY = 'spokenWordStyle.fill';

export const setCaptionsSchema = z.object({
    enabled: z
        .boolean()
        .optional()
        .describe(
            'Turn captions on or off. Applies to the whole video unless scene_ids narrows it to specific scenes.',
        ),
    scene_ids: z
        .array(z.string())
        .optional()
        .describe(
            'Scenes to turn captions on or off for, by scene_id from the video state. Omit to apply `enabled` to the whole video. Only narrows `enabled` — the design, size, position, colors and font are always global.',
        ),
    design_id: z
        .string()
        .optional()
        .describe('The caption design to apply globally. Omit to list available designs.'),
    text_size: z.number().optional().describe('Caption font size.'),
    position: z
        .enum(CAPTION_POSITIONS)
        .optional()
        .describe('Vertical placement of the captions in the frame.'),
    text_color: z.string().optional().describe('Caption text color as a #RRGGBB hex string.'),
    spoken_word_color: z
        .string()
        .optional()
        .describe('Highlight color for the word currently being spoken.'),
    font_family: z.string().optional(),
});
export type SetCaptionsArgs = z.infer<typeof setCaptionsSchema>;

export class SetCaptionsTool extends VideoStateTool<typeof setCaptionsSchema> {
    protected override readonly schema = setCaptionsSchema;
    protected override readonly toolName = TOOL_NAMES.setCaptions;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        "Control captions for a voiceover video. The caption design, size, vertical position, colors and font are global and apply to every voicedriven scene group. Captions only apply to voicedriven (voiceover or talking-head) videos. New videos inherit the user's last-used captions setting (usually off) — leave that alone unless the user asks for a change, or a reference video or recipe you are following specifies what the captions should be.";

    public override getToolDisplayName(): string {
        return 'Setting captions';
    }

    public get statusIcon(): string {
        return 'closed_caption';
    }

    public async execute(args: SetCaptionsArgs, context: ToolExecutionContext) {
        let videoEdited = false;
        const result = this.applyCaptionSettings(args, context.parentActionGroup, () => {
            videoEdited = true;
        });
        // The caption operations don't record undo history themselves, so record the turn's
        // edits as one step, the way the editor's caption controls do.
        if (videoEdited) {
            this.checkpointUnderTurn(context.parentActionGroup);
        }
        return result;
    }

    private applyCaptionSettings(
        args: SetCaptionsArgs,
        parentActionGroup: string,
        onVideoEdited: () => void,
    ): string {
        const {
            enabled,
            scene_ids,
            design_id,
            text_size,
            position,
            text_color,
            spoken_word_color,
            font_family,
        } = args;

        const voicedrivenSceneGroups = this.luminary.sceneGroups.filter(isVoiceDrivenSceneGroup);

        if (voicedrivenSceneGroups.length === 0) {
            if (enabled === false) {
                return 'Success: No captions to turn off — this video has no voiceover.';
            }
            return 'Error: This video has no voiceover or talking-head scene groups, so captions cannot be configured. Captions only apply to voicedriven videos.';
        }

        const templateId = this.luminary.template?.id;
        let designs: ReturnType<typeof getCaptionsSceneDesignsForTemplate> = [];
        if (templateId && this.getState) {
            try {
                designs = getCaptionsSceneDesignsForTemplate(this.getState(), templateId);
            } catch {
                // Template scene designs may be unavailable in the current state;
                // fall back to an empty list so caption visibility can still be toggled.
                designs = [];
            }
        }
        const designIds = designs.map((design) => design.id);

        const operations = makeOperationsCallable(
            {
                setSceneGroupCaptionsVisibilityOperation,
                setCaptionsSceneDesignOperation,
                setCaptionsTextSizeOperation,
                setCaptionsColorOperation,
                setCaptionsFontUrlOperation,
                setAssetPositionTransformationOperation,
                setHasUserChangedAssetPositionOperation,
                setSceneDisplayCaptionsOperation,
            },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const recordingEdit =
            <Params extends unknown[], Result>(operation: (...params: Params) => Result) =>
            (...params: Params): Result => {
                onVideoEdited();
                return operation(...params);
            };
        const setSceneGroupCaptionsVisibility = recordingEdit(
            operations.setSceneGroupCaptionsVisibility,
        );
        const setCaptionsSceneDesign = recordingEdit(operations.setCaptionsSceneDesign);
        const setCaptionsTextSize = recordingEdit(operations.setCaptionsTextSize);
        const setCaptionsColor = recordingEdit(operations.setCaptionsColor);
        const setCaptionsFontUrl = recordingEdit(operations.setCaptionsFontUrl);
        const setAssetPositionTransformation = recordingEdit(
            operations.setAssetPositionTransformation,
        );
        const setHasUserChangedAssetPosition = recordingEdit(
            operations.setHasUserChangedAssetPosition,
        );
        const setSceneDisplayCaptions = recordingEdit((sceneId: string, displayCaptions: boolean) =>
            operations.setSceneDisplayCaptions(sceneId, displayCaptions, false, parentActionGroup),
        );

        const nothingRequested =
            design_id === undefined &&
            enabled === undefined &&
            text_size === undefined &&
            position === undefined &&
            text_color === undefined &&
            spoken_word_color === undefined &&
            font_family === undefined;

        if (nothingRequested) {
            return this.describeCurrentState(voicedrivenSceneGroups, designs);
        }

        if (design_id !== undefined && !designIds.includes(design_id)) {
            const available = designIds.length
                ? designs
                      .map((design) => `${design.id} (${design.display_name ?? 'unnamed'})`)
                      .join(', ')
                : 'none available';
            return `Error: Unknown caption design "${design_id}". Available designs: ${available}`;
        }

        // Luminary stores colors as integers, so parse before writing: passing the hex string
        // through unchanged double-prefixes the '#' and renders as the wrong color.
        const parsedColors: { property: string; value: number; label: string }[] = [];
        for (const [label, property, value] of [
            ['text_color', TEXT_COLOR_PROPERTY, text_color],
            ['spoken_word_color', SPOKEN_WORD_COLOR_PROPERTY, spoken_word_color],
        ] as const) {
            if (value === undefined) {
                continue;
            }
            const parsed = parseHexColor(value);
            if (parsed === null) {
                return `Error: ${label} must be a hex color like #RRGGBB (got "${value}").`;
            }
            parsedColors.push({ property, value: parsed, label: value });
        }

        const summaryParts: string[] = [];
        // Requested states that were already in effect, reported apart from the changes so the
        // agent can tell a no-op from a toggle that silently failed.
        const alreadySetParts: string[] = [];

        // The design reset rebuilds the caption asset and drops its position, so apply the
        // design first and let any requested position land on the new asset.
        if (design_id !== undefined) {
            setCaptionsSceneDesign(design_id);
            summaryParts.push(`Applied caption design "${design_id}".`);
        }

        if (enabled !== undefined) {
            // An empty scene_ids names no scenes, which is the same request as omitting it:
            // apply `enabled` to the whole video rather than failing on a technicality.
            if (scene_ids !== undefined && scene_ids.length > 0) {
                // A per-scene toggle rides on scene.displayCaptions, which suppresses the global
                // track on that scene alone. The track itself stays as it was: narrowing to a
                // scene must never turn the whole video's captions off.
                const scoped = this.setSceneCaptionsVisibility(
                    scene_ids,
                    enabled,
                    setSceneDisplayCaptions,
                );
                if ('error' in scoped) {
                    return scoped.error;
                }
                if (scoped.summary) {
                    summaryParts.push(scoped.summary);
                }
                if (scoped.alreadySetSummary) {
                    alreadySetParts.push(scoped.alreadySetSummary);
                }

                // The engine only draws captions where the group's track is visible AND the scene
                // displays them, so scoping `enabled: true` while the video-level track is off
                // would render nothing while reporting success. Turn the track on and suppress it
                // on every other scene, which is the only reading of "captions on for these
                // scenes" that the renderer can actually honour.
                if (enabled && !voicedrivenSceneGroups.some((group) => group.captions?.visible)) {
                    for (const sceneGroup of voicedrivenSceneGroups) {
                        setSceneGroupCaptionsVisibility(sceneGroup.id, true);
                    }
                    const scopedIds = new Set(scoped.sceneIds);
                    for (const scene of this.luminary.scenes) {
                        if (!scopedIds.has(scene.id) && scene.sceneGroup?.captions?.visible) {
                            setSceneDisplayCaptions(scene.id, false);
                        }
                    }
                    summaryParts.push(
                        'Correction: the video-level caption track was off, so nothing would have rendered. It was turned on and hidden on every other scene, so only the scenes above show captions.',
                    );
                }
            } else {
                const trackAlreadySet = voicedrivenSceneGroups.every(
                    (sceneGroup) => Boolean(sceneGroup.captions?.visible) === enabled,
                );
                if (!trackAlreadySet) {
                    for (const sceneGroup of voicedrivenSceneGroups) {
                        setSceneGroupCaptionsVisibility(sceneGroup.id, enabled);
                    }
                    summaryParts.push(
                        `Captions turned ${enabled ? 'on' : 'off'} for the whole video.`,
                    );
                }
                if (trackAlreadySet) {
                    alreadySetParts.push(
                        `Captions are already ${enabled ? 'on' : 'off'} for the whole video.`,
                    );
                }
                // A scene hidden on its own (by an earlier scoped call or the editor's per-scene
                // toggle) stays blank while the track reports on. That may be deliberate, so it is
                // left alone, but "already on" alone reads as captions showing everywhere.
                const hiddenSceneIds = enabled ? this.individuallyHiddenCaptionSceneIds() : [];
                if (hiddenSceneIds.length > 0) {
                    const scenes = `${hiddenSceneIds.length === 1 ? 'scene' : 'scenes'} ${hiddenSceneIds.join(', ')}`;
                    (trackAlreadySet ? alreadySetParts : summaryParts).push(
                        `Warning: captions are hidden for ${scenes}, so they do not show there. To show them, call again with \`enabled: true\` and those \`scene_ids\`.`,
                    );
                }
            }
        } else if (scene_ids !== undefined && scene_ids.length > 0) {
            summaryParts.push(
                'Ignored scene_ids: it only narrows `enabled`, and no `enabled` value was given. The design, size, position, colors and font are always global.',
            );
        }

        if (font_family !== undefined) {
            const templateFamily = resolveTemplateFontFamily(this.luminary, font_family);
            const fontIndex =
                templateFamily === null
                    ? null
                    : getTemplateFontIndex(this.luminary, templateFamily);
            if (fontIndex === null) {
                return `Error: ${describeUnavailableTemplateFont(this.luminary, font_family)}`;
            }
            setCaptionsFontUrl(fontIndex);
            summaryParts.push(`Set caption font to ${templateFamily}.`);
        }

        for (const { property, value, label } of parsedColors) {
            if (property === SPOKEN_WORD_COLOR_PROPERTY && !this.hasSpokenWordHighlight()) {
                // The write would be silently dropped (a plain caption asset has no
                // spokenWordStyle) or land on a style the design never renders, and the video
                // state omits it either way — so say so instead of claiming a change.
                summaryParts.push(
                    `Did NOT set the spoken-word color: this caption design has no spoken-word highlight. Pick a design that highlights the spoken word first.`,
                );
                continue;
            }
            setCaptionsColor(property, value);
            summaryParts.push(
                property === TEXT_COLOR_PROPERTY
                    ? `Set caption text color to ${label}.`
                    : `Set spoken-word color to ${label}.`,
            );
        }

        // Size before position: the rendered height depends on the font size, and the
        // vertical placement is computed from that height. The bounds are read here rather than
        // up front because applying a design rebuilds the caption asset with that design's own
        // text box, which moves the ceiling.
        if (text_size !== undefined) {
            const sizeBounds = this.getTextSizeBounds();
            if (!sizeBounds) {
                return 'Error: Cannot determine the usable caption text size range for this template. Call this tool with no arguments to inspect the current caption state.';
            }
            const clamped = Math.min(Math.max(text_size, sizeBounds.min), sizeBounds.max);
            setCaptionsTextSize(clamped);
            summaryParts.push(
                clamped === text_size
                    ? `Set caption text size to ${text_size}.`
                    : `Set caption text size to ${clamped}: ${text_size} is outside the settable range (${sizeBounds.min}-${sizeBounds.max}).`,
            );
            // Above this the text overflows its box and wraps to more lines, which the editor
            // flags but still permits — so warn instead of shrinking the size back down.
            if (
                sizeBounds.fitsWithoutOverflow !== null &&
                clamped > sizeBounds.fitsWithoutOverflow
            ) {
                summaryParts.push(
                    `Note: ${clamped} is above the largest size this caption design fits on one line (${sizeBounds.fitsWithoutOverflow}), so captions will wrap onto more lines. Check the result and reduce the size if it crowds the frame.`,
                );
            }
        }

        if (position !== undefined) {
            const moved = this.positionCaptions(
                voicedrivenSceneGroups,
                position,
                setAssetPositionTransformation,
                setHasUserChangedAssetPosition,
            );
            if (!moved) {
                return 'Error: Could not resolve the caption asset to reposition. Make sure captions are enabled and a caption design is applied first.';
            }
            summaryParts.push(`Moved captions to ${position}.`);
        }

        if (summaryParts.length === 0 && alreadySetParts.length > 0) {
            const alreadySet = alreadySetParts.join(' ');
            return `No change: ${alreadySet.charAt(0).toLowerCase()}${alreadySet.slice(1)}`;
        }
        return `Success: ${[...summaryParts, ...alreadySetParts].join(' ')}`;
    }

    /**
     * Turn the global caption track on or off for specific scenes, via `scene.displayCaptions` —
     * the flag that suppresses the global track on a single scene. The track itself is untouched,
     * so narrowing to a scene never turns the whole video's captions off.
     *
     * Resolves every scene_id before writing any of them: a partially-applied toggle would leave
     * the video in a state the agent didn't ask for and can't easily tell apart from success.
     */
    private setSceneCaptionsVisibility(
        sceneIds: string[],
        enabled: boolean,
        setSceneDisplayCaptions: (sceneId: string, displayCaptions: boolean) => void,
    ): { summary: string; alreadySetSummary: string; sceneIds: string[] } | { error: string } {
        const state = this.getState?.() as RootState | undefined;
        if (!state) {
            return {
                error: 'Error: Cannot resolve scene_ids without the editor state. Omit scene_ids to turn captions on or off for the whole video.',
            };
        }

        const resolved: { id: string; shortId: string }[] = [];
        for (const sceneId of sceneIds) {
            const resolution = resolveSceneById(this.luminary, state, sceneId);
            if ('error' in resolution) {
                return { error: `Error: ${resolution.error}` };
            }
            resolved.push({ id: resolution.scene.id, shortId: sceneId });
        }

        const changed: string[] = [];
        const alreadySet: string[] = [];
        for (const { id, shortId } of resolved) {
            const scene = this.luminary.scenes.find((candidate) => candidate.id === id);
            // `displayCaptions` is computed — a scene carrying its own fancy caption reports false
            // so the global track doesn't double up — but writing to it sets a sticky override that
            // the getter checks first and nothing here can clear. Skip the write when the scene
            // already reports what was asked for, so turning captions "on" for a scene that already
            // shows them can't silently defeat that suppression for a design placed later.
            if (scene && scene.displayCaptions === enabled) {
                alreadySet.push(shortId);
                continue;
            }
            setSceneDisplayCaptions(id, enabled);
            changed.push(shortId);
        }

        const visibility = enabled ? 'on' : 'off';
        const describeScenes = (shortIds: string[]) =>
            `${shortIds.length === 1 ? 'scene' : 'scenes'} ${shortIds.join(', ')}`;
        return {
            summary:
                changed.length > 0
                    ? `Captions turned ${visibility} for ${describeScenes(changed)}. The rest of the video is unchanged.`
                    : '',
            alreadySetSummary:
                alreadySet.length > 0
                    ? `Captions were already ${visibility} for ${describeScenes(alreadySet)}.`
                    : '',
            sceneIds: resolved.map(({ id }) => id),
        };
    }

    private describeCurrentState(
        voicedrivenSceneGroups: ReturnType<typeof this.luminary.sceneGroups.filter>,
        designs: ReturnType<typeof getCaptionsSceneDesignsForTemplate>,
    ): string {
        const currentlyVisible = voicedrivenSceneGroups.some((sg) => sg.captions.visible);
        const currentDesignId = voicedrivenSceneGroups[0]?.captions.sceneDesignId || 'none';
        const designLines = designs.length
            ? designs
                  .map((design) => `- ${design.id}: ${design.display_name ?? '(unnamed)'}`)
                  .join('\n')
            : '(none available)';

        const captionAsset = this.getCaptionAsset();
        const bounds = this.getTextSizeBounds();

        const lines = [
            `Captions are currently ${currentlyVisible ? 'on' : 'off'}.`,
            `Current caption design: ${currentDesignId}.`,
        ];
        if (captionAsset && typeof captionAsset.textSize === 'number') {
            lines.push(`Current caption text size: ${captionAsset.textSize}.`);
        }
        if (bounds) {
            lines.push(`Settable text size range: ${bounds.min}-${bounds.max}.`);
            if (bounds.fitsWithoutOverflow !== null) {
                lines.push(
                    `Largest size that fits on one line with this design: ${bounds.fitsWithoutOverflow} (bigger is allowed; captions then wrap onto more lines).`,
                );
            }
        }
        lines.push('Available caption designs:', designLines);
        return lines.join('\n');
    }

    /**
     * Whether the caption design in use actually renders a spoken-word highlight. Mirrors the
     * predicate the video state serializer uses, so a reported change is one the agent can then
     * see in the state.
     */
    private hasSpokenWordHighlight(): boolean {
        const asset = this.getCaptionAsset();
        return Boolean(
            asset && VideoData.isFancyCaptionAsset(asset) && asset.isSpokenWordStyleEnabled,
        );
    }

    /**
     * Best-effort: the selector throws when no voicedriven scene group resolves, and callers
     * that only toggle visibility or swap the design must keep working without a caption asset.
     */
    private getCaptionAsset() {
        try {
            return firstCaptionAsset(this.luminary);
        } catch {
            return undefined;
        }
    }

    /**
     * The settable range, matching what the editor's caption text-size control can actually
     * reach. That ceiling is NOT the template's `text_max_size`: the control's slider is
     * bounded by the fit ceiling, and its presets come from `default_text_size_options`, which
     * is an independent field. A preset above the fit ceiling is greyed out but still applies
     * on click, so a person's real maximum is the larger of the two — and `text_max_size`
     * itself is reachable by no editor path. `fitsWithoutOverflow` is the largest size at
     * which the tallest word still fits the caption design's box; it stays advisory rather
     * than a clamp, for the same reason those presets stay clickable. Null when the template
     * bounds can't be read.
     */
    private getTextSizeBounds(): {
        min: number;
        max: number;
        fitsWithoutOverflow: number | null;
    } | null {
        const templateId = this.luminary.template?.id;
        if (!templateId || !this.getState) {
            return null;
        }
        let templateMin: number | undefined;
        let templateMax: number | undefined;
        let defaultSizes: ReturnType<typeof getTemplateTextDefaultSizes> | undefined;
        try {
            templateMin = getTemplateTextMinSize(this.getState(), templateId) as number | undefined;
            templateMax = getTemplateTextMaxSize(this.getState(), templateId) as number | undefined;
            defaultSizes = getTemplateTextDefaultSizes(this.getState(), templateId);
        } catch {
            return null;
        }
        if (typeof templateMin !== 'number' || typeof templateMax !== 'number') {
            return null;
        }

        const captionAsset = this.getCaptionAsset();
        const fittedMax = captionAsset?.getFontSizeToFitTallestText?.({
            min: templateMin,
            max: templateMax,
        })?.fontSize;

        // The presets the editor offers, clamped to the template's own ceiling so a stray
        // oversized option can't push the range past what the template can render.
        const presetsWithinTemplate = (defaultSizes ?? []).filter(
            (size) => typeof size === 'number' && size <= templateMax,
        );
        const largestPreset =
            presetsWithinTemplate.length > 0 ? Math.max(...presetsWithinTemplate) : undefined;

        return {
            min: templateMin,
            max: Math.max(largestPreset ?? templateMin, fittedMax ?? templateMin),
            fitsWithoutOverflow: typeof fittedMax === 'number' ? fittedMax : null,
        };
    }

    /**
     * Scenes whose captions are switched off on their own via `scene.displayCaptions`. A scene
     * carrying its own fancy caption is skipped: the engine suppresses the global track there so
     * the two don't double up, and it reads as hidden for that reason.
     */
    private individuallyHiddenCaptionSceneIds(): string[] {
        const hidden: string[] = [];
        for (const scene of this.luminary.scenes) {
            if (
                !scene.sceneGroup ||
                !isVoiceDrivenSceneGroup(scene.sceneGroup) ||
                scene.displayCaptions ||
                scene.getAssetsByType(VideoData.FANCY_CAPTION_ASSET_TYPE).length > 0
            ) {
                continue;
            }
            hidden.push(sceneDisplayId(this.luminary, scene.id));
        }
        return hidden;
    }

    private positionCaptions(
        voicedrivenSceneGroups: ReturnType<typeof this.luminary.sceneGroups.filter>,
        position: CaptionPosition,
        setAssetPositionTransformation: (args: {
            sceneGroupId: string;
            assetId: string;
            x: number;
            y: number;
        }) => void,
        setHasUserChangedAssetPosition: (args: {
            sceneGroupId: string;
            assetId: string;
            value: boolean;
        }) => void,
    ): boolean {
        let movedAny = false;

        for (const sceneGroup of voicedrivenSceneGroups) {
            const asset = sceneGroup.captions?.asset ?? this.getCaptionAsset();
            if (!asset) {
                continue;
            }

            const frameHeight = asset.root?.referenceResolution?.height;
            // The rendered text box: boundsWithoutAnimation is empty until the renderer has drawn
            // the caption, so fall back to the design-anchored max text area, which is
            // render-state-independent and is what the video state reports.
            const renderedHeight = asset.boundsWithoutAnimation?.height;
            const assetHeight =
                typeof renderedHeight === 'number' && renderedHeight > 0
                    ? renderedHeight
                    : asset.approximateMaxBounds?.height;
            const currentX = asset.transformation?.position?.x;
            // Caption assets are bottom-anchored by default (anchor.y === 1), so `position` is the
            // anchor point, not the top-left corner: converting a desired top edge into a position
            // means adding back the anchored fraction of the height.
            const anchorY = asset.transformation?.anchor?.y ?? 0;
            if (
                typeof frameHeight !== 'number' ||
                typeof assetHeight !== 'number' ||
                typeof currentX !== 'number'
            ) {
                continue;
            }

            const margin = Math.round(frameHeight * CAPTION_EDGE_MARGIN_RATIO);
            const topEdge = (() => {
                switch (position) {
                    case 'top':
                        return margin;
                    case 'bottom':
                        return frameHeight - assetHeight - margin;
                    case 'center':
                    default:
                        return Math.round((frameHeight - assetHeight) / 2);
                }
            })();
            const y = Math.round(topEdge + anchorY * assetHeight);

            setAssetPositionTransformation({
                sceneGroupId: sceneGroup.id,
                assetId: asset.id,
                x: currentX,
                y,
            });
            // Without this the next design change silently resets the placement.
            setHasUserChangedAssetPosition({
                sceneGroupId: sceneGroup.id,
                assetId: asset.id,
                value: true,
            });
            movedAny = true;
        }

        return movedAny;
    }
}

/** Keeps top/bottom placements clear of the frame edge and the safe area. */
const CAPTION_EDGE_MARGIN_RATIO = 0.08;
